/**
 * firebase/index.js — SDK initialisation.
 *
 * Changes from v1:
 *  - The reCAPTCHA Enterprise site key was hardcoded in the source while every
 *    other Firebase value came from the environment. It now comes from the
 *    environment too, so staging and production can differ.
 *  - App Check debug tokens are wired up for local development; v1 had no
 *    escape hatch, so App Check either blocked localhost or was left off.
 *  - Emulator support, so the app can be developed without touching
 *    production data.
 *  - Firestore uses persistent local caching with multi-tab support, which
 *    removes a large share of the read cost and makes the app usable on a
 *    flaky connection.
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const missing = Object.entries(firebaseConfig)
  .filter(([key, value]) => !value && key !== 'measurementId')
  .map(([key]) => key);

if (missing.length) {
  // Fail loudly at boot rather than with a cryptic SDK error later.
  // eslint-disable-next-line no-console
  console.error(
    `Firebase is not configured. Missing: ${missing.join(', ')}. ` +
      'Copy .env.example to .env and fill it in.'
  );
}

const app = initializeApp(firebaseConfig);

const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true';

/* App Check -------------------------------------------------------- */

if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_APPCHECK_DEBUG_TOKEN) {
  // Must be set before initializeAppCheck.
  // eslint-disable-next-line no-restricted-globals
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
}

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
if (recaptchaSiteKey && !useEmulators) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

/* Services --------------------------------------------------------- */

const auth = getAuth(app);
auth.useDeviceLanguage();

const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

const storage = getStorage(app);
const functions = getFunctions(app, process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1');

if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

export { app, auth, db, storage, functions, httpsCallable };
